import { useCollectionView } from '../../app/use-collection-view.js';
import {
  Button,
  DateRangePicker,
  IconButton,
  Modal,
  Select,
  StatusPill,
  formatCivilRange,
  todayCivilDate,
  useIsWideViewport,
  useSnackbar,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, PlayCircle, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  getApiError,
  protocolApplicationsApi,
  protocolsApi,
  type ProtocolApplication,
  type ProtocolApplicationSort,
  type ProtocolApplicationStatusFilter,
} from '../../lib/api.js';
import { factTimeZone, formatDateTime } from '../../lib/time-zone.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  Pagination,
  RequestAlert,
  SortBar,
  SortableColumn,
} from '../../app/data-view.js';
import type { ProtocolApplicationsView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';

export interface ProtocolApplicationCapabilities {
  canManage: boolean;
}

const statusOptions: Array<{ value: ProtocolApplicationStatusFilter; label: string }> = [
  { value: 'ALL', label: 'Todas' },
  { value: 'PLANNED', label: 'Planejadas' },
  { value: 'APPLIED', label: 'Aplicadas' },
];
const columns: Array<{ sort: ProtocolApplicationSort; label: string }> = [
  { sort: 'appointmentStartsAt', label: 'Atendimento' },
  { sort: 'clientDisplayName', label: 'Cliente' },
  { sort: 'createdAt', label: 'Registrado em' },
];
const unitLabels = { UNIT: 'un', MILLILITER: 'ml', GRAM: 'g' } as const;

function useProtocolOptions() {
  return useQuery({
    queryKey: ['company', 'protocol-applications', 'protocol-options'],
    queryFn: () =>
      protocolsApi.list({
        activity: 'ACTIVE',
        service: 'ALL',
        sort: 'displayName',
        direction: 'asc',
        limit: 100,
        offset: 0,
      }),
    retry: false,
  });
}

export function CompanyProtocolApplications({
  capabilities,
  consolidated = false,
  timeZone,
  view,
  onView,
}: {
  capabilities: ProtocolApplicationCapabilities;
  /// Sem unidade escolhida numa empresa de várias: lista as alcançadas, com a origem, e só consulta.
  consolidated?: boolean;
  timeZone: string;
  view: ProtocolApplicationsView;
  onView: ViewSetter<ProtocolApplicationsView>;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const collectionView = useCollectionView();
  const wide = useIsWideViewport() && collectionView === 'list';
  const protocols = useProtocolOptions();
  const [registerOpen, setRegisterOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<ProtocolApplication>();
  const [removeError, setRemoveError] = useState<string>();
  const filters = {
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    status: view.status,
    protocol: view.protocol,
    ...(view.from ? { from: view.from } : {}),
    ...(view.to ? { to: view.to } : {}),
    sort: view.sort,
    direction: view.direction,
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: ['company', 'protocol-applications', filters],
    queryFn: () => protocolApplicationsApi.list(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const opened = page.data?.applications.find((item) => item.id === view.application);

  function toggleSort(column: ProtocolApplicationSort) {
    if (view.sort === column) onView({ direction: view.direction === 'asc' ? 'desc' : 'asc' });
    else onView({ sort: column, direction: column === 'clientDisplayName' ? 'asc' : 'desc' });
  }

  async function removePlanned(application: ProtocolApplication) {
    setRemoveError(undefined);
    try {
      await protocolApplicationsApi.removePlanned(application.id);
      await queryClient.invalidateQueries({ queryKey: ['company', 'protocol-applications'] });
      setRemoveTarget(undefined);
      snackbar.success('Planejamento removido.');
    } catch (error) {
      setRemoveError(getApiError(error));
    }
  }

  const protocolOptions = [
    { value: 'ALL', label: 'Todos os protocolos' },
    ...(protocols.data?.protocols ?? []).map((protocol) => ({
      value: protocol.id,
      label: protocol.displayName,
    })),
  ];
  const total = page.data?.total ?? 0;
  const chips = [
    ...(view.status === 'ALL'
      ? []
      : [
          {
            key: 'status',
            label: statusOptions.find((item) => item.value === view.status)!.label,
            onRemove: () => onView({ status: 'ALL' }),
          },
        ]),
    ...(protocols.data?.protocols ?? [])
      .filter((protocol) => protocol.id === view.protocol)
      .map((protocol) => ({
        key: 'protocol',
        label: protocol.displayName,
        onRemove: () => onView({ protocol: 'ALL' }),
      })),
    ...((view.from ?? view.to)
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from: view.from, to: view.to }, 'Qualquer dia'),
            onRemove: () => onView({ from: null, to: null }),
          },
        ]
      : []),
  ];

  return (
    <section aria-labelledby="company-protocol-applications-title">
      <PageHeader
        trail="Operação / Protocolos"
        id="company-protocol-applications-title"
        title="Aplicações de protocolo"
        description="O protocolo que cada atendimento vai usar e o que foi efetivamente aplicado."
        actions={
          <>
            {capabilities.canManage ? (
              <CreateButton onClick={() => setRegisterOpen(true)}>Registrar protocolo</CreateButton>
            ) : null}
          </>
        }
      />

      {consolidated ? (
        <p
          role="status"
          className="mb-5 rounded-2xl border border-surface-border bg-surface-card p-4 text-sm text-content-secondary"
        >
          Visão consolidada: aplicações de todas as suas unidades, só para consulta. Para planejar,
          aplicar ou remover, escolha uma unidade no seletor acima.
        </p>
      ) : null}

      <FilterBar
        searchId="protocol-application-search"
        searchLabel="Buscar aplicação"
        searchPlaceholder="Cliente, protocolo ou serviço"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() => onView({ status: 'ALL', protocol: 'ALL', from: null, to: null })}
        count={`${total} ${total === 1 ? 'aplicação' : 'aplicações'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'aplicação' : 'aplicações'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <FilterGroup
          label="Situação"
          description="Filtrar aplicações por situação"
          options={statusOptions}
          value={view.status}
          onChange={(status) => onView({ status })}
        />
        <FilterGroup
          label="Protocolo"
          description="Filtrar aplicações por protocolo"
          options={protocolOptions}
          value={view.protocol}
          onChange={(protocol) => onView({ protocol })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Atendimento</span>
          <DateRangePicker
            label="Dias de atendimento"
            value={{ from: view.from, to: view.to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={todayCivilDate(timeZone)}
          />
        </div>
      </FilterBar>

      {protocols.error ? <ErrorCard message={getApiError(protocols.error)} /> : null}

      {page.isLoading ? (
        <LoadingCard label="Carregando aplicações" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={ClipboardCheck}
          title="Nenhuma aplicação encontrada"
          description="Ajuste os filtros ou registre o protocolo de um atendimento."
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          {wide ? null : (
            <div>
              <SortBar
                label="Ordenação das aplicações"
                columns={columns}
                sort={view.sort}
                direction={view.direction}
                onToggle={toggleSort}
              />
              <ul className="divide-y divide-surface-border">
                {page.data?.applications.map((application) => (
                  <li key={application.id} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <strong className="block truncate text-sm">
                          {application.clientDisplayName}
                        </strong>
                        <span className="mt-1 block truncate text-xs text-content-muted">
                          {application.protocolDisplayName} ·{' '}
                          {formatDateTime(
                            application.appointmentStartsAt,
                            factTimeZone(application, timeZone, consolidated),
                          )}
                        </span>
                        {consolidated ? (
                          <span className="mt-1 block truncate text-xs text-content-faint">
                            {application.businessUnitName}
                          </span>
                        ) : null}
                        <div className="mt-2">
                          <StatusPill
                            tone={application.status === 'APPLIED' ? 'positive' : 'progress'}
                          >
                            {application.status === 'APPLIED' ? 'Aplicada' : 'Planejada'}
                          </StatusPill>
                        </div>
                      </div>
                      <Actions
                        application={application}
                        capabilities={capabilities}
                        onOpen={() => onView({ application: application.id })}
                        onRemove={() => setRemoveTarget(application)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {wide ? (
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">Aplicações de protocolo da empresa</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  {columns.map((column) => (
                    <SortableColumn
                      key={column.sort}
                      column={column.sort}
                      label={column.label}
                      sort={view.sort}
                      direction={view.direction}
                      onToggle={toggleSort}
                    />
                  ))}
                  {consolidated ? (
                    <th className="w-36 px-5 py-3 text-xs font-semibold text-content-muted">
                      Unidade
                    </th>
                  ) : null}
                  <th className="w-36 px-5 py-3 text-xs font-semibold text-content-muted">
                    Situação
                  </th>
                  <th className="w-24">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.applications.map((application) => (
                  <tr key={application.id}>
                    <td className="px-5 py-4">
                      <strong className="block truncate text-sm">
                        {formatDateTime(
                          application.appointmentStartsAt,
                          factTimeZone(application, timeZone, consolidated),
                        )}
                      </strong>
                      <span className="mt-1 block truncate text-xs text-content-muted">
                        {application.serviceDisplayName} · {application.protocolDisplayName}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {application.clientDisplayName}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {formatDateTime(application.createdAt, timeZone)}
                    </td>
                    {consolidated ? (
                      <td className="truncate px-5 py-4 text-xs text-content-muted">
                        {application.businessUnitName}
                      </td>
                    ) : null}
                    <td className="px-5 py-4">
                      <StatusPill tone={application.status === 'APPLIED' ? 'positive' : 'progress'}>
                        {application.status === 'APPLIED' ? 'Aplicada' : 'Planejada'}
                      </StatusPill>
                      {application.plannedProtocolDisplayName ? (
                        <span className="mt-1 block truncate text-xs text-content-faint">
                          Planejado: {application.plannedProtocolDisplayName}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-4">
                      <Actions
                        application={application}
                        capabilities={capabilities}
                        onOpen={() => onView({ application: application.id })}
                        onRemove={() => setRemoveTarget(application)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'aplicação' : 'aplicações')}
          />
        </div>
      )}

      {registerOpen ? (
        <RegisterDialog
          timeZone={timeZone}
          onOpenChange={() => setRegisterOpen(false)}
          onSaved={async (message) => {
            await queryClient.invalidateQueries({ queryKey: ['company', 'protocol-applications'] });
            setRegisterOpen(false);
            snackbar.success(message);
          }}
        />
      ) : null}
      {opened ? (
        <ApplicationDialog
          application={opened}
          timeZone={factTimeZone(opened, timeZone, consolidated)}
          onOpenChange={() => onView({ application: undefined })}
        />
      ) : null}
      {removeTarget ? (
        <Modal
          open
          onOpenChange={() => setRemoveTarget(undefined)}
          title="Remover planejamento"
          description={`O protocolo ${removeTarget.protocolDisplayName} deixa de estar previsto para esse atendimento. Nada do que já aconteceu é apagado.`}
          footer={
            <>
              <Button variant="outline" onClick={() => setRemoveTarget(undefined)}>
                Manter
              </Button>
              <Button onClick={() => void removePlanned(removeTarget)}>Remover</Button>
            </>
          }
        >
          {removeError ? <RequestAlert message={removeError} /> : null}
        </Modal>
      ) : null}
    </section>
  );
}

function Actions({
  application,
  capabilities,
  onOpen,
  onRemove,
}: {
  application: ProtocolApplication;
  capabilities: ProtocolApplicationCapabilities;
  onOpen: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <IconButton
        label="Ver aplicação"
        icon={<ClipboardCheck className="size-4" />}
        onClick={onOpen}
      />
      {capabilities.canManage && application.status === 'PLANNED' ? (
        <IconButton
          label="Remover planejamento"
          tone="danger"
          icon={<Trash2 className="size-4" />}
          onClick={onRemove}
        />
      ) : null}
    </div>
  );
}

/// Registrar é um diálogo só, com duas saídas: planejar o palpite ou aplicar agora. Quem atende
/// escolhe o protocolo na hora, e o planejado vem apenas pré-selecionado quando existe.
function RegisterDialog({
  timeZone,
  onOpenChange,
  onSaved,
}: {
  timeZone: string;
  onOpenChange: (open: boolean) => void;
  onSaved: (message: string) => Promise<void>;
}) {
  const [appointmentId, setAppointmentId] = useState('');
  const [protocolId, setProtocolId] = useState('');
  const [requestError, setRequestError] = useState<string>();
  const appointments = useQuery({
    queryKey: ['company', 'protocol-applications', 'appointments'],
    queryFn: () => protocolApplicationsApi.listAppointments(),
    retry: false,
  });
  const protocols = useProtocolOptions();

  async function submit(intent: 'PLAN' | 'APPLY') {
    setRequestError(undefined);
    try {
      const input = { appointmentId, protocolId };
      if (intent === 'PLAN') {
        await protocolApplicationsApi.plan(input);
        await onSaved('Protocolo planejado para o atendimento.');
      } else {
        await protocolApplicationsApi.apply(input);
        await onSaved('Protocolo aplicado no atendimento.');
      }
    } catch (error) {
      setRequestError(getApiError(error));
    }
  }

  const catalog = protocols.data?.protocols ?? [];
  const appointmentOptions = (appointments.data ?? []).map((appointment) => ({
    value: appointment.id,
    label: `${formatDateTime(appointment.startsAt, timeZone)} · ${appointment.clientDisplayName} · ${appointment.serviceDisplayName}`,
  }));
  const protocolOptions = catalog.map((protocol) => ({
    value: protocol.id,
    label: protocol.displayName,
  }));
  const ready = Boolean(appointmentId && protocolId);

  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title="Registrar protocolo"
      description="Escolha o atendimento e o protocolo. Planejar não move o Estoque; aplicar registra que o atendimento aconteceu com esse processo."
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button variant="outline" disabled={!ready} onClick={() => void submit('PLAN')}>
            Planejar
          </Button>
          <Button disabled={!ready} onClick={() => void submit('APPLY')}>
            <PlayCircle className="size-4" />
            Aplicar agora
          </Button>
        </>
      }
    >
      {appointments.isLoading || protocols.isLoading ? (
        <LoadingCard label="Carregando atendimentos" />
      ) : appointments.isError || protocols.isError ? (
        <ErrorCard message="Não foi possível carregar os atendimentos." />
      ) : appointmentOptions.length === 0 ? (
        <EmptyCard
          icon={ClipboardCheck}
          title="Nenhum atendimento disponível"
          description="Marque o atendimento na Agenda para registrar o protocolo dele."
        />
      ) : (
        <div className="grid gap-3">
          <Select
            label="Atendimento"
            value={appointmentId}
            options={appointmentOptions}
            onValueChange={setAppointmentId}
          />
          <Select
            label="Protocolo"
            value={protocolId}
            options={protocolOptions}
            onValueChange={setProtocolId}
          />
          {requestError ? <RequestAlert message={requestError} /> : null}
        </div>
      )}
    </Modal>
  );
}

/// O retrato como ficou: etapas e previsto congelados no instante da aplicação. Planejada ainda não
/// tem retrato, e dizer isso é mais honesto do que mostrar uma lista vazia sem explicação.
function ApplicationDialog({
  application,
  timeZone,
  onOpenChange,
}: {
  application: ProtocolApplication;
  timeZone: string;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title={application.protocolDisplayName}
      description={`${application.clientDisplayName} · ${formatDateTime(application.appointmentStartsAt, timeZone)}`}
    >
      <div className="grid gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill tone={application.status === 'APPLIED' ? 'positive' : 'progress'}>
            {application.status === 'APPLIED' ? 'Aplicada' : 'Planejada'}
          </StatusPill>
          {application.appliedAt ? (
            <span className="text-xs text-content-muted">
              {formatDateTime(application.appliedAt, timeZone)}
              {application.appliedByDisplayName ? ` · ${application.appliedByDisplayName}` : ''}
            </span>
          ) : null}
        </div>
        {application.plannedProtocolDisplayName ? (
          <p className="text-xs text-content-muted">
            O planejado era {application.plannedProtocolDisplayName} e foi trocado no atendimento.
          </p>
        ) : null}
        {application.status === 'PLANNED' ? (
          <p className="text-sm text-content-muted">
            O retrato das etapas e do material previsto é feito quando o protocolo é aplicado.
          </p>
        ) : (
          <ol className="grid gap-3">
            {application.steps.map((step) => (
              <li key={step.id} className="rounded-2xl border border-surface-border p-4 text-sm">
                <strong className="block">
                  {step.position}. {step.title}
                </strong>
                {step.description ? (
                  <span className="mt-1 block text-xs text-content-muted">{step.description}</span>
                ) : null}
                <ul className="mt-2 grid gap-1">
                  {step.materials.length === 0 ? (
                    <li className="text-xs text-content-faint">Sem material previsto</li>
                  ) : (
                    step.materials.map((material) => (
                      <li key={material.id} className="text-xs text-content-muted">
                        {material.productDisplayName} · {material.quantity}{' '}
                        {unitLabels[material.baseUnit]}
                      </li>
                    ))
                  )}
                </ul>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Modal>
  );
}
